import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import { SectionHeading, EmptyState } from 'components/Erp/Presentation';
import React, { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { hasAccess } from 'utils/access'
import { deleteBranch } from 'service/AdminPanelService'
import { getErpErrorMessage } from 'service/ErpService'
import BranchDeleteDialog from '../BranchDeleteDialog/BranchDeleteDialog.tsx'
import { branchEditKey } from '../branchEdit'
import ResponsiveTable from './ResponsiveTable'
import AuditButton from './AuditButton'
import { MapPin, Map , Pencil, Save, X, ChevronLeft, ChevronRight, Network, Plus, Trash2 } from 'lucide-react'
import { DateTime, StatusBadge } from 'components/Erp/Display'
import styles from '../AdminPanelPage.module.scss'
import MiniMap from 'pages/ReportCard/MiniMap'

const BranchesList = ({ base, baseType = "ТОП-базы", branches = [], branchesFilter = '', setBranchesFilter = () => {}, openCreateModal, loadBranchesByBasePage = () => {}, editingBranch, editPayloads = {}, setEditPayloads = () => {}, startEdit = () => {}, saveEdit = () => {}, cancelEdit = () => {}, branchesPagination = {} }) => {
  useInterfaceLanguage();
  const user = useSelector(state => state.user)
  const [deleting, setDeleting] = useState(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const deleteBusyRef = useRef(false)
  const [deleteError, setDeleteError] = useState('')
  const [deleteSuccess, setDeleteSuccess] = useState('')
  const [saving, setSaving] = useState(null)
  const saveBusyRef = useRef(false)
  const getBaseKey = (type) => {
    if (type === 'ТОП-базы') return 'top_base'
    if (type === 'ТЭЦ') return 'chpp'
    if (type === 'Соц. Объекты и МСУ' || type === 'privatepoint') return 'private_point'
    return 'top_base'
  }
  const getBaseLabel = (type) => {
    if (type === 'ТОП-базы') return tr("ТОП-базы")
    if (type === 'ТЭЦ') return tr("ТЭЦ")
    if (type === 'Соц. Объекты и МСУ' || type === 'privatepoint') return tr("Соц.объекта или МСУ")
    return tr("базы")
  }
  const baseKey = getBaseKey(baseType)
  const baseLabel = getBaseLabel(baseType)
  const saveBranch = async id => {
    if (saveBusyRef.current) return
    saveBusyRef.current = true
    setSaving(id.brId)
    try {
      await saveEdit('branch', id)
    } finally {
      saveBusyRef.current = false
      setSaving(null)
    }
  }
  const confirmDelete = async pin => {
    if (!deleting || deleteBusyRef.current) return
    deleteBusyRef.current = true
    setDeleteBusy(true)
    setDeleteError('')
    try {
      await deleteBranch(deleting.id, pin)
      const currentPage = branchesPagination.page || 1
      const page = branches.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage
      setDeleting(null)
      setDeleteSuccess(tr('Филиал удалён.'))
      await loadBranchesByBasePage(base.id, page, branchesFilter || '')
    } catch (error) {
      setDeleteError(getErpErrorMessage(error))
    } finally {
      deleteBusyRef.current = false
      setDeleteBusy(false)
    }
  }
  return (
    <div style={{ marginTop: 12 }}>
      {deleteSuccess && <p role="status">{deleteSuccess}</p>}
      <SectionHeading icon={Network} title={tr("Филиалы {{value0}}", { value0: baseLabel })} as="h3" actions={<div className={styles.actionGroup}>
                  <button className={styles.smallBtn} onClick={() => openCreateModal('branch', { [baseKey]: base.id, [`${baseKey}_name`]: base.name })}>
          <Plus size={14} aria-hidden="true"/>{tr(" Создать филиал")}</button>
        </div>} />
      <div style={{ marginTop: 8 }}>
        <div className={styles.searchRow}>
          <input className={styles.searchInput} placeholder={tr("Поиск...")} value={branchesFilter || ''} onChange={(e) => setBranchesFilter(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') loadBranchesByBasePage(base.id, 1, branchesFilter || '') }} />

        </div>
        {(branches && branches.length) ? (
          <>
            <ResponsiveTable className={styles.table}>
              <thead>
                <tr>
                  <th>№</th>
                  <th>{tr("Название")}</th>
                  <th>{tr("Адрес")}</th>
                  <th>{tr("Гео")}</th>
                  <th>{tr("Создано")}</th>
                  <th>{tr("Статус")}</th>
                  <th>{tr("Действия")}</th>
                </tr>
              </thead>
              <tbody>
                {branches.filter(br => {
                  const q = (branchesFilter || '').toLowerCase()
                  if (!q) return true
                  return (br.name && br.name.toLowerCase().includes(q)) || (br.address && br.address.toLowerCase().includes(q))
                }).map((br, index) => {
                  const branchId = { [baseKey]: base.id, brId: br.id }
                  const editKey = branchEditKey(branchId)
                  const editField = (field, value) => setEditPayloads(payloads => ({ ...payloads, [editKey]: { ...payloads[editKey], [field]: value } }))
                  return (
                  <React.Fragment key={br.id}>
                    <tr>
                      <td>{tr(index + 1)}</td>
                      <td>{tr(br.name)}</td>
                      <td>{tr(br.address || '-')}</td>
                      <td>
                        {br.geo ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexDirection: 'column' }}>
                            <div className={styles.actionGroup}>
                              <button
                                onClick={() => window.open(`https://www.google.com/maps?q=${br.geo}`, '_blank')}
                                className={styles.geoLinkBtn}
                                title={tr("Открыть в Google Maps")}
                              >
                                <Map size={13}/>{tr(" Карты")}</button>
                              <button
                                onClick={() => {
                                  const elem = document.getElementById(`map-branch-${br.id}`);
                                  if (elem) elem.style.display = elem.style.display === 'none' ? 'block' : 'none';
                                }}
                                className={styles.mapToggleBtn}
                                title={tr("Показать/скрыть карту")}
                              >
                                <MapPin size={13}/>
                              </button>
                            </div>
                            <div id={`map-branch-${br.id}`} style={{ width: 200, height: 150, display: 'none' }}>
                              <MiniMap geo={br.geo} />
                            </div>
                          </div>
                        ) : '-'}
                      </td>
                      <td><DateTime value={br.created_at}/></td>
                      <td><StatusBadge tone={br.is_active ? 'success' : 'danger'}>{br.is_active ? tr("Активен") : tr("Неактивен")}</StatusBadge></td>
                      <td>
                        <div className={styles.actionGroup}>
                          <button disabled={saving != null || deleteBusy} className={`${styles.smallBtn} ${styles.editBtn}`} aria-expanded={editingBranch?.[base.id] === br.id} onClick={() => startEdit('branch', branchId, { name: br.name, address: br.address, geo: br.geo, is_active: br.is_active })}><Pencil size={14} aria-hidden="true"/>{tr(" Изменить")}</button><AuditButton section={baseKey === 'chpp' ? 'chpps' : baseKey === 'private_point' ? 'private_points' : 'top_bases'} objectId={br.id} objectType="tabel.Branch"/>
                          {hasAccess(user, 'branches_delete') && <button disabled={deleteBusy || saving != null} className={`${styles.smallBtn} ${styles.deleteBtn}`} aria-haspopup="dialog" aria-expanded={deleting?.id === br.id} aria-label={tr('Удалить филиал {{value0}}', { value0: br.name })}
                            onClick={() => { setDeleteError(''); setDeleteSuccess(''); setDeleting(br) }}><Trash2 size={14} aria-hidden="true" />{tr('Удалить')}</button>}
                        </div>
                      </td>
                    </tr>
                    {tr(editingBranch && editingBranch[base.id] === br.id && (
                      <tr>
                        <td colSpan={7}>
                          <div style={{ padding: 8 }}>
                            <label htmlFor={`branch-name-${baseKey}-${br.id}`}>{tr("Название")}</label>
                            <input disabled={saving === br.id} id={`branch-name-${baseKey}-${br.id}`} value={editPayloads[editKey]?.name || ''} onChange={event => editField('name', event.target.value)} />
                            <label style={{ marginLeft: 8 }} htmlFor={`branch-address-${baseKey}-${br.id}`}>{tr("Адрес")}</label>
                            <input disabled={saving === br.id} id={`branch-address-${baseKey}-${br.id}`} value={editPayloads[editKey]?.address || ''} onChange={event => editField('address', event.target.value)} />
                            <label style={{ marginLeft: 8 }} htmlFor={`branch-geo-${baseKey}-${br.id}`}>{tr("Гео")}</label>
                            <input disabled={saving === br.id} id={`branch-geo-${baseKey}-${br.id}`} value={editPayloads[editKey]?.geo || ''} onChange={event => editField('geo', event.target.value)} />
                            <label style={{ marginLeft: 8 }} htmlFor={`branch-active-${baseKey}-${br.id}`}>{tr("Активен")}</label>
                            <input disabled={saving === br.id} id={`branch-active-${baseKey}-${br.id}`} type="checkbox" checked={editPayloads[editKey]?.is_active || false} onChange={event => editField('is_active', event.target.checked)} />
                            <div className={styles.actionGroup} style={{ marginTop: 8 }}>
                              <button disabled={saving === br.id} className={styles.btn_pin} onClick={() => saveBranch(branchId)}><Save size={14} aria-hidden="true"/>{tr(" Сохранить")}</button>
                              <button disabled={saving === br.id} className={styles.btn_pin_close} onClick={() => cancelEdit('branch', branchId)}><X size={14} aria-hidden="true"/>{tr(" Отмена")}</button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                )})}
              </tbody>
            </ResponsiveTable>
            {tr((!branchesFilter && branchesPagination && branchesPagination.count > 0) && (
              <div className={styles.pagination}>
                <button disabled={!branchesPagination.previous} onClick={() => loadBranchesByBasePage(base.id, (branchesPagination.page || 1) - 1)}><ChevronLeft size={14} aria-hidden="true"/>{tr(" Пред.")}</button>
                <span>{tr("Стр. ")}{tr(branchesPagination.page || 1)}{tr(" — Всего: ")}{tr(branchesPagination.count)}</span>
                <button disabled={!branchesPagination.next} onClick={() => loadBranchesByBasePage(base.id, (branchesPagination.page || 1) + 1)}><ChevronRight size={14} aria-hidden="true"/>{tr(" След.")}</button>
              </div>
            ))}
          </>
        ) : (
          <div><EmptyState icon={Network} title={tr("Филиалы не найдены")} compact /></div>
        )}
      </div>
      {deleting && <BranchDeleteDialog name={deleting.name} busy={deleteBusy} error={deleteError}
        onClose={() => { if (!deleteBusy) { setDeleting(null); setDeleteError('') } }} onConfirm={confirmDelete} />}
    </div>
  )
}

export default BranchesList
